import type { TFunction } from "i18next";

import { devWarn } from "@/util/dev.mjs";
import { getLocale } from "@/util/i18n-helper.mjs";

export const greaterThanAndNotEq = (val1: number, val2: number) => {
  if (val1 === val2) return null;
  return val1 > val2;
};

export const lessThanAndNotEq = (val1: number, val2: number) => {
  if (val1 === val2) return null;
  return val1 < val2;
};

export function toFixedNumber(number: number, precision: number) {
  return typeof number === "number" ? Number(number.toFixed(precision)) : null;
}

export function sanitizeNumber(number: number) {
  return Number.isNaN(number) || !Number.isFinite(number) ? 0 : number;
}

export function sanitizeDisplayNumber(number: number) {
  return typeof number === "number" && !Number.isNaN(number) ? number : "-";
}

export function calcRate(
  num: number,
  total: number,
  fraction: number | null = null,
) {
  const result = num / (total > 0 ? total : 1);
  const sanitized = sanitizeNumber(result);
  return typeof fraction === "number"
    ? toFixedNumber(sanitized, fraction)
    : sanitized;
}

export function displayRate(
  count: number,
  totalCount: number,
  tiesCount = 0,
  fraction = 0,
) {
  if (totalCount > 0) {
    const rate = calcRate(count + tiesCount * 0.5, totalCount);
    return rate.toLocaleString(getLocale(), {
      minimumFractionDigits: fraction,
      maximumFractionDigits: fraction,
      style: "percent",
    });
  }
  return "-%";
}

export function convertQueryToWriteStatePath(
  init?: string | string[][] | Record<string, string> | URLSearchParams,
) {
  const urlParams = new URLSearchParams(init);
  return urlParams.toString();
}

export const formatGameTime = (sec: number) =>
  `${~~(sec / 60)}:${`00${~~(sec % 60)}`.slice(-2)}`;

// https://gist.github.com/nblackburn/875e6ff75bc8ce171c758bf75f304707#gistcomment-3677597
export function camelCaseToKebabCase(str: string) {
  return str
    .replace(/\B([A-Z])(?=[a-z])/g, "-$1")
    .replace(/\B([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase();
}

export function camelToSnakeCase(str: string) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export function delay(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// inputs {duration}s or {duration}ms
export function durationToMs(duration: string, fallbackUnits = 1): number {
  return (
    parseFloat(duration) *
    (duration.endsWith("s")
      ? 1000
      : duration.endsWith("ms")
        ? 1
        : fallbackUnits)
  );
}

// converts a symbol to string, useful for converting symbol to a react key
export function symbolToString(symbol: symbol) {
  // symbol?.toString() gives us string or null/undef
  // String() converts null to "null"
  return String(symbol?.toString());
}

export function assignRef<T>(
  ref: React.MutableRefObject<T> | ((node: T) => void),
  value: T,
) {
  if (typeof ref === "function") return ref(value);
  if (ref) ref.current = value;
}

export function removeFromArray<T>(arr: T[], ...items: T[]) {
  let count = 0;
  for (const item of items) {
    const idx = arr.indexOf(item);
    if (idx === -1) continue;
    arr.splice(idx, 1);
    count++;
  }
  return count;
}

// simple helper to get the last item of an iterable without constructing an Array.
// compatible with all iterables that have `length` (incl Array, HTMLCollectionOf)
export const lastOf = <T extends Iterable<unknown> & { length: number }>(
  arr: T,
): T extends Iterable<infer U> ? U : never => arr[arr.length - 1];

export const dateToUTC = (date: Date) =>
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );

export const timestampToUTC = (timestamp: number) =>
  dateToUTC(new Date(timestamp));

/// `indexOf`, but returns the length of the array if the item is not found
export function indexOrLength<T>(array: T[], item: T) {
  const index = array.indexOf(item);
  return index === -1 ? array.length : index;
}

// idempotent version of Element.append
export function safeAppend(parent: Node, ...children: Node[]): boolean {
  let appended = false;
  for (const child of children)
    if (child.parentElement !== parent) {
      parent.appendChild(child);
      appended = true;
    }
  return appended;
}

export function renderText(t: TFunction, text: string | Translation) {
  if (typeof text === "string") return text;
  if (Array.isArray(text)) return t(...text);
  devWarn("renderText", "unexpected text type", typeof text, text);
}

// pushes an item to an array, returning a cleanup function to remove it
export function pushClean<T = unknown>(arr: T[], item: T) {
  arr.push(item);
  return () => removeFromArray(arr, item);
}

const APP_ROUTE_REGEXP = /^\/(?:app\/|v(?=\d))[^/]*/;
export function stripAppVersion(url: string) {
  return url.replace(APP_ROUTE_REGEXP, "");
}

export function setOrRemoveProperty(obj, key, value) {
  if (value) obj.setProperty(key, value);
  else obj.removeProperty(key);
}

export function getDurationPerDay(milliseconds, totalDurationInMillis) {
  const seconds = milliseconds / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;
  const totalDays = totalDurationInMillis / (1000 * 60 * 60 * 24) || 1;
  if (seconds / totalDays < 60) return { seconds: seconds / totalDays };
  if (minutes / totalDays < 60) return { minutes: minutes / totalDays };
  return { hours: hours / totalDays };
}
